import type { AuthSessionSnapshot } from '@/lib/common';

export type OptimizationScopeFilter = 'my' | 'supervised' | 'all';

export type OptimizationStatusValue =
  | 'Active'
  | 'Implemented'
  | 'Rejected'
  | 'Inactive';

export const ALL_OPTIMIZATION_STATUSES: OptimizationStatusValue[] = [
  'Active',
  'Implemented',
  'Rejected',
  'Inactive',
];

export interface OptimizationFilterState {
  scopeFilter: OptimizationScopeFilter;
  cloudAccountId: string;
  resourceOwnerId: string;
  subscriptionId: string;
  systemId: string;
  departmentId: string;
  statuses: OptimizationStatusValue[] | 'all';
  chartScope: 'resource_owners' | 'systems' | 'departments';
}

export interface OptimizationFilterOptions {
  cloudAccounts: { id: number; name: string }[];
  departments: { id: number; name: string }[];
  subscriptions: { subscription_id: string; display_name: string }[];
  systems: { id: number; name: string }[];
  users: { id: number; firstName?: string; username: string }[];
}

export function serializeStatuses(
  statuses: OptimizationStatusValue[] | 'all',
): string {
  if (statuses === 'all') {
    return 'all';
  }
  if (statuses.length === 0) {
    return 'Active';
  }
  if (statuses.length === ALL_OPTIMIZATION_STATUSES.length) {
    return 'all';
  }
  return statuses.join(',');
}

export function buildOptimizationQueryParams(
  filters: OptimizationFilterState,
): string {
  const params = new URLSearchParams();
  params.set('scopeFilter', filters.scopeFilter);
  if (filters.cloudAccountId) {
    params.set('cloudAccountId', filters.cloudAccountId);
  }
  if (filters.resourceOwnerId) {
    params.set('resourceOwnerId', filters.resourceOwnerId);
  }
  if (filters.subscriptionId) {
    params.set('subscriptionId', filters.subscriptionId);
  }
  if (filters.systemId) {
    params.set('systemId', filters.systemId);
  }
  if (filters.departmentId) {
    params.set('departmentId', filters.departmentId);
  }
  params.set('statusFilter', serializeStatuses(filters.statuses));
  return params.toString();
}

export type OptimizationUser = Pick<
  AuthSessionSnapshot,
  | 'id'
  | 'is_admin'
  | 'is_sponsor'
  | 'is_resourceOwner'
  | 'is_cloudAccountOwner'
  | 'is_departmentOwner'
  | 'is_systemOwner'
  | 'is_technical'
>;
